import axios from 'axios';
import { adsApi } from '@/api/client';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import { validateCampaignBudget } from '@/utils/campaignCreate';
import { budgetPeriodFromType } from '@/utils/marketplaceBudgetLimits';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';

export type CampaignBudgetAdjustMode =
  | 'INCREASE_AMOUNT'
  | 'INCREASE_PERCENT'
  | 'SET_AMOUNT'
  | 'DECREASE_AMOUNT'
  | 'DECREASE_PERCENT';

export const CAMPAIGN_BUDGET_ADJUST_MODES: CampaignBudgetAdjustMode[] = [
  'INCREASE_AMOUNT',
  'INCREASE_PERCENT',
  'SET_AMOUNT',
  'DECREASE_AMOUNT',
  'DECREASE_PERCENT',
];

export function campaignBudgetAdjustModeLabel(mode: CampaignBudgetAdjustMode): string {
  switch (mode) {
    case 'INCREASE_AMOUNT':
      return 'Increase budget by (₹)';
    case 'INCREASE_PERCENT':
      return 'Increase budget by (%)';
    case 'SET_AMOUNT':
      return 'Set budget to (₹)';
    case 'DECREASE_AMOUNT':
      return 'Decrease budget by (₹)';
    case 'DECREASE_PERCENT':
      return 'Decrease budget by (%)';
  }
}

export type EditableCampaignBudgetTarget = {
  adProduct: CampaignAdProduct;
  campaignId: string;
  budget: number;
  budgetType: string;
};

export type CampaignBudgetBulkUpdate = {
  campaignId: string;
  adProduct: CampaignAdProduct;
  budget: number;
  budgetType?: string;
  previousBudget?: number;
};

type CampaignBudgetMutationResult = {
  campaignId?: string | null;
  adProduct?: string;
  status?: string;
  message?: string;
  newBudget?: number;
  previousBudget?: number;
};

function roundBudget(value: number): number {
  return Math.round(value * 100) / 100;
}

export function campaignSelectionKey(campaign: SpCampaignNormalized): string {
  return `${campaign.adProduct}-${campaign.campaignId}`;
}

export function resolveEditableCampaignBudget(
  campaign: SpCampaignNormalized
): EditableCampaignBudgetTarget | null {
  if (!campaign.campaignId) return null;
  if (!Number.isFinite(campaign.budget) || campaign.budget <= 0) return null;
  return {
    adProduct: campaign.adProduct,
    campaignId: campaign.campaignId,
    budget: campaign.budget,
    budgetType: campaign.budgetType || 'DAILY',
  };
}

export function computeAdjustedBudget(
  currentBudget: number,
  mode: CampaignBudgetAdjustMode,
  value: number,
  adProduct: CampaignAdProduct = 'SP',
  budgetType: string = 'DAILY'
): { budget: number | null; error: string | null } {
  if (!Number.isFinite(value) || value <= 0) {
    return { budget: null, error: 'Enter a value greater than 0' };
  }
  if (mode === 'DECREASE_PERCENT' && value > 100) {
    return { budget: null, error: 'Decrease percentage cannot exceed 100%' };
  }

  let next = currentBudget;
  switch (mode) {
    case 'INCREASE_AMOUNT':
      next = currentBudget + value;
      break;
    case 'INCREASE_PERCENT':
      next = currentBudget * (1 + value / 100);
      break;
    case 'SET_AMOUNT':
      next = value;
      break;
    case 'DECREASE_AMOUNT':
      next = currentBudget - value;
      break;
    case 'DECREASE_PERCENT':
      next = currentBudget * (1 - value / 100);
      break;
    default:
      return { budget: null, error: 'Unsupported budget adjustment mode' };
  }

  const rounded = roundBudget(next);
  const validationError = validateCampaignBudget(rounded, adProduct, budgetPeriodFromType(budgetType));
  if (validationError) {
    return { budget: null, error: validationError };
  }
  return { budget: rounded, error: null };
}

export function buildBulkCampaignBudgetUpdates(
  campaigns: SpCampaignNormalized[],
  mode: CampaignBudgetAdjustMode,
  value: number
): { updates: CampaignBudgetBulkUpdate[]; errors: string[] } {
  const updates: CampaignBudgetBulkUpdate[] = [];
  const errors: string[] = [];

  for (const campaign of campaigns) {
    const { budget, error } = computeAdjustedBudget(
      campaign.budget,
      mode,
      value,
      campaign.adProduct,
      campaign.budgetType
    );
    if (error || budget == null) {
      errors.push(`${campaign.name}: ${error ?? 'Invalid budget'}`);
      continue;
    }
    updates.push({
      campaignId: campaign.campaignId,
      adProduct: campaign.adProduct,
      budget,
      budgetType: campaign.budgetType || 'DAILY',
      previousBudget: campaign.budget,
    });
  }

  return { updates, errors };
}

function parseMutationResults(data: unknown): CampaignBudgetMutationResult[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const results = raw.results;
  if (!Array.isArray(results)) return [];
  return results as CampaignBudgetMutationResult[];
}

const BUDGET_UPDATE_FALLBACK = 'Failed to update budget';

export function campaignBudgetApiErrorMessage(
  error: unknown,
  fallback = BUDGET_UPDATE_FALLBACK
): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (detail && typeof detail === 'object' && detail !== null && 'message' in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message.trim();
    }
    return fallback;
  }
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return fallback;
}

export async function applyCampaignBudgetUpdate(
  profileId: string,
  target: EditableCampaignBudgetTarget,
  newBudget: number
): Promise<void> {
  const validationError = validateCampaignBudget(
    newBudget,
    target.adProduct,
    budgetPeriodFromType(target.budgetType)
  );
  if (validationError) {
    throw new Error(validationError);
  }

  try {
    if (target.adProduct === 'SP') {
      const res = await adsApi.updateSpCampaignBudget(profileId, target.campaignId, {
        budget: newBudget,
        budgetType: target.budgetType,
      });
      if (!res.success) throw new Error(BUDGET_UPDATE_FALLBACK);
      return;
    }

    if (target.adProduct === 'SB') {
      const res = await adsApi.updateSbCampaignBudget(profileId, target.campaignId, {
        budget: newBudget,
      });
      if (!res.success) throw new Error(BUDGET_UPDATE_FALLBACK);
      return;
    }

    const res = await adsApi.updateSdCampaignBudget(profileId, target.campaignId, {
      budget: newBudget,
    });
    if (!res.success) throw new Error(BUDGET_UPDATE_FALLBACK);
  } catch (error) {
    throw new Error(campaignBudgetApiErrorMessage(error));
  }
}

export async function applyBulkCampaignBudgetUpdates(
  profileId: string,
  updates: CampaignBudgetBulkUpdate[]
): Promise<CampaignBudgetMutationResult[]> {
  if (!updates.length) {
    throw new Error('Select at least one campaign');
  }

  for (const update of updates) {
    const validationError = validateCampaignBudget(
      update.budget,
      update.adProduct,
      budgetPeriodFromType(update.budgetType)
    );
    if (validationError) {
      throw new Error(validationError);
    }
  }

  const res = await adsApi.applyCampaignBudgetUpdates(profileId, {
    updates: updates.map((update) => ({
      campaignId: update.campaignId,
      adProduct: update.adProduct,
      budget: update.budget,
      budgetType: update.budgetType,
      previousBudget: update.previousBudget,
    })),
  });
  if (!res.success) throw new Error('Failed to update campaign budgets');
  return parseMutationResults(res.data);
}

export function formatBudgetUpdateSuccessMessage(newBudget: number): string {
  return `Budget updated to ${formatCurrencyValue(newBudget)}`;
}

export function summarizeBulkBudgetResults(results: CampaignBudgetMutationResult[]): string {
  const successCount = results.filter((item) => item.status === 'success').length;
  const failureCount = results.length - successCount;
  if (failureCount === 0) {
    return `Updated budget for ${successCount} campaign${successCount === 1 ? '' : 's'}`;
  }
  return `Updated ${successCount} campaign${successCount === 1 ? '' : 's'}, ${failureCount} failed`;
}
